import React from 'react';
import { Col, Row, Table } from 'reactstrap';
import logo from '../../assets/images/NGK-LOGO.png';
import type { SingleOrder } from '../../graphql/queries/orders';
import {
  formatOrderTotal,
  getOrderLineItemDisplayName,
} from '../../utils/orderLineItemMeta';
import {
  formatInvoiceAddress,
  formatInvoiceOrderDate,
} from '../../pages/Invoices/invoicePrintUtils';

export interface InvoicePrintContentProps {
  order: SingleOrder;
  invoiceNumber: string;
}

const InvoicePrintContent: React.FC<InvoicePrintContentProps> = ({ order, invoiceNumber }) => {
  const orderNumberLabel = `Order #${order.orderNumber ?? order.databaseId}`;

  const billingAddress = formatInvoiceAddress([
    [order.billing?.firstName, order.billing?.lastName].filter(Boolean).join(' '),
    order.billing?.address1,
    order.billing?.address2,
    order.billing?.city,
    order.billing?.state,
    order.billing?.postcode,
    order.billing?.country,
  ]);

  const shippingAddress = formatInvoiceAddress([
    [order.shipping?.firstName, order.shipping?.lastName].filter(Boolean).join(' '),
    order.shipping?.address1,
    order.shipping?.address2,
    order.shipping?.city,
    order.shipping?.state,
    order.shipping?.postcode,
    order.shipping?.country,
  ]);

  const lineItems = order.lineItems?.nodes ?? [];
  const shippingTotal = (order.shippingLines?.nodes ?? []).reduce(
    (sum, line) => sum + Number(String(line.total ?? '0').replace(/[^0-9.-]/g, '')),
    0
  );
  const lineItemsSubtotal = lineItems.reduce(
    (sum, item) => sum + Number(String(item.subtotal ?? item.total ?? '0').replace(/[^0-9.-]/g, '')),
    0
  );

  return (
    <div className="p-4 bg-white">
      <div className="invoice-title">
        <div className="d-flex align-items-start">
          <div className="flex-grow-1">
            <div className="mb-4">
              <img src={logo} alt="NGK Tours" height="24" />
            </div>
          </div>
          <div className="flex-shrink-0">
            <div className="mb-4">
              <h4 className="float-end font-size-16">Invoice {invoiceNumber}</h4>
              <p className="float-end text-muted mb-0 clearfix w-100 text-end">
                {orderNumberLabel}
              </p>
            </div>
          </div>
        </div>
        {shippingAddress ? (
          <address>
            {shippingAddress.split(',').map((part, key) => (
              <React.Fragment key={key}>
                <span>{part.trim()}</span>
                <br />
              </React.Fragment>
            ))}
          </address>
        ) : (
          <address className="text-muted">No shipping address</address>
        )}
      </div>
      <hr className="my-4" />
      <Row>
        <Col sm="6">
          <div>
            <h5 className="font-size-15 mb-3">Billed To:</h5>
            {billingAddress.split(',').map((part, key) => (
              <React.Fragment key={key}>
                <span>{part.trim()}</span>
                <br />
              </React.Fragment>
            ))}
          </div>
        </Col>
        <Col sm="6">
          <div>
            <h5 className="font-size-15">Order Date:</h5>
            <p>{formatInvoiceOrderDate(order.date)}</p>
          </div>
          <div className="mt-4">
            <h5 className="font-size-15">Payment Method:</h5>
            <p className="mb-1">{order.paymentMethodTitle || '—'}</p>
            <p>{order.billing?.email || '—'}</p>
          </div>
        </Col>
      </Row>
      <div className="py-2 mt-3">
        <h3 className="font-size-15 fw-bold">Order summary</h3>
      </div>
      <div className="p-4 border rounded">
        <div className="table-responsive">
          <Table className="table-nowrap align-middle mb-0">
            <thead>
              <tr>
                <th style={{ width: '70px' }}>No.</th>
                <th>Item</th>
                <th className="text-end" style={{ width: '120px' }}>
                  Price
                </th>
              </tr>
            </thead>
            <tbody>
              {lineItems.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-muted">
                    No line items.
                  </td>
                </tr>
              ) : (
                lineItems.map((item, index) => (
                  <tr key={`${item.productId ?? 'item'}-${index}`}>
                    <td>{index + 1}</td>
                    <td>
                      <h5 className="font-size-15 mb-1">
                        {getOrderLineItemDisplayName(item.product?.node?.name, item.metaData)}
                      </h5>
                      {item.quantity != null && (
                        <p className="font-size-13 text-muted mb-0">Qty: {item.quantity}</p>
                      )}
                    </td>
                    <td className="text-end">
                      {formatOrderTotal(item.total ?? item.subtotal, order.currency)}
                    </td>
                  </tr>
                ))
              )}
              <tr>
                <td colSpan={2} className="text-end">
                  Sub Total
                </td>
                <td className="text-end">
                  {formatOrderTotal(lineItemsSubtotal, order.currency)}
                </td>
              </tr>
              <tr>
                <td colSpan={2} className="border-0 text-end">
                  <strong>Shipping</strong>
                </td>
                <td className="border-0 text-end">
                  {formatOrderTotal(shippingTotal, order.currency)}
                </td>
              </tr>
              <tr>
                <td colSpan={2} className="border-0 text-end">
                  <strong>Total</strong>
                </td>
                <td className="border-0 text-end">
                  <h4 className="m-0">{formatOrderTotal(order.total, order.currency)}</h4>
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default InvoicePrintContent;
